import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  User, Lock, Bell, Moon, Globe, LogOut, ChevronRight, ChevronDown,
  Eye, EyeOff, Check, Shield, Sparkles, Settings, Mail, MessageCircle, 
  Calendar, Clock, BookOpen, PenLine
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  savePushSubscription, 
  deletePushSubscription, 
  getUserNotificationSettings, 
  updateNotificationSettings,
  sendTestPush,
  getVapidPublicKey,
  sendWeeklyReportEmail,
  sendTestEmail,
  startNotificationScheduler,
  stopNotificationScheduler,
  sendScheduledNotification,
  getBarakahProfile,
  saveBarakahProfile
} from '../lib/api';
import { isAdminEmail } from '../lib/admin';

const FOCUS_AREAS = ['Salah', 'Quran', 'Hifz', 'Dhikr', 'Character', 'Family'];

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-primary' : 'bg-stone-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function SectionCard({ title, icon: Icon, iconBg, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full px-6 py-4 border-b border-stone-100 flex items-center justify-between ${iconBg} hover:bg-stone-100/50 transition-colors`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-[#292524]" />
          <h2 className="font-heading font-semibold text-[#292524] text-sm uppercase tracking-wide">
            {title}
          </h2>
        </div>
        <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-6">{children}</div>}
    </div>
  );
}

function NotifSettingRow({ icon: Icon, iconColor, title, description, time, onTimeChange, showTime, children }) {
  return (
    <div className="py-3.5 border-b border-stone-100 last:border-b-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColor}`}>
            <Icon className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-[#292524] text-sm">{title}</p>
            {description && <p className="text-xs text-[#78716C] mt-0.5 truncate">{description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {showTime && time !== undefined && (
            <div className="flex items-center gap-1.5 bg-stone-50 rounded-lg px-2 py-1 border border-stone-200">
              <Clock className="w-3 h-3 text-stone-400" />
              <input
                type="time"
                value={time}
                onChange={(e) => onTimeChange(e.target.value)}
                className="text-xs font-mono text-[#292524] bg-transparent outline-none w-[60px] cursor-pointer"
              />
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Profile({ user }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const lang = i18n.language;

  // Password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  // Email state
  const [notificationEmail, setNotificationEmail] = useState(user?.email || '');
  const [emailLoading, setEmailLoading] = useState(false);

  // Notification states
  const [notifSettings, setNotifSettings] = useState({
    push_enabled: false,
    prayer_reminders: false,
    dhikr_reminders: false,
    verse_of_day: false,
    reflection_reminders: false,
    weekly_report_enabled: false,
    prayer_reminder_time: '04:30',
    dhikr_reminder_time: '09:00',
    verse_reminder_time: '07:00',
    reflection_reminder_time: '20:00',
  });
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [testPushLoading, setTestPushLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);

  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [barakahProfile, setBarakahProfile] = useState(null);
  const [barakahProfileSaving, setBarakahProfileSaving] = useState(false);

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-US', {
        year: 'numeric',
        month: 'long',
      })
    : null;

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [dbSettings, profileSettings] = await Promise.all([
          getUserNotificationSettings(),
          getBarakahProfile().catch(() => null),
        ]);
        setNotifSettings(prev => ({
          ...prev,
          push_enabled: dbSettings.push_enabled ?? false,
          prayer_reminders: dbSettings.prayer_reminders ?? false,
          dhikr_reminders: dbSettings.dhikr_reminders ?? false,
          verse_of_day: dbSettings.verse_of_day ?? false,
          reflection_reminders: dbSettings.reflection_reminders ?? false,
          weekly_report_enabled: dbSettings.weekly_report_enabled ?? false,
          prayer_reminder_time: dbSettings.prayer_reminder_time || '04:30',
          dhikr_reminder_time: dbSettings.dhikr_reminder_time || '09:00',
          verse_reminder_time: dbSettings.verse_reminder_time || '07:00',
          reflection_reminder_time: dbSettings.reflection_reminder_time || '20:00',
        }));
        if (profileSettings) {
          setBarakahProfile(profileSettings);
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    };
    
    loadSettings();
    checkPushSubscription();
  }, []);

  // Start notification scheduler when settings change
  useEffect(() => {
    if (pushSubscribed && Notification.permission === 'granted') {
      startNotificationScheduler(notifSettings, lang);
    }
    return () => stopNotificationScheduler();
  }, [notifSettings, pushSubscribed, lang]);

  const checkPushSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setPushSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking push subscription:', error);
      }
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error(lang === 'hr' ? 'Push notifikacije nisu podržane' : 'Push notifications not supported');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error(lang === 'hr' ? 'Dozvola za notifikacije odbijena' : 'Notification permission denied');
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      let vapidKey;
      try { vapidKey = await getVapidPublicKey(); } catch { 
        vapidKey = 'BJeAz378TmfPOf96DvP5hdfRhQCvWT9qJxcNgOtq75KFWVgVwfjcRp33VYLgNMN5qMMAWyy5oM9YxkWo5i_slxY';
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
      try {
        await savePushSubscription(subscription);
        await updateNotifSetting({ push_enabled: true });
      } catch (dbError) {
        console.error('Error saving subscription to DB:', dbError);
      }
      localStorage.setItem('pushSubscription', JSON.stringify(subscription));
      setPushSubscribed(true);
      toast.success(lang === 'hr' ? 'Push notifikacije aktivirane!' : 'Push notifications activated!');
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error(lang === 'hr' ? 'Greška pri aktivaciji notifikacija' : 'Error activating notifications');
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        try {
          await deletePushSubscription(subscription.endpoint);
          await updateNotifSetting({ push_enabled: false });
        } catch (dbError) {
          console.error('Error removing subscription from DB:', dbError);
        }
        await subscription.unsubscribe();
        localStorage.removeItem('pushSubscription');
        setPushSubscribed(false);
        toast.info(lang === 'hr' ? 'Push notifikacije deaktivirane' : 'Push notifications deactivated');
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
    }
  };

  const updateNotifSetting = async (updates) => {
    const newSettings = { ...notifSettings, ...updates };
    setNotifSettings(newSettings);
    try {
      await updateNotificationSettings(updates);
    } catch (error) {
      console.error('Error saving setting:', error);
    }
    
    // Send confirmation notification when enabling a category
    if (pushSubscribed && Notification.permission === 'granted') {
      const typeMap = {
        prayer_reminders: 'prayer',
        dhikr_reminders: 'dhikr',
        verse_of_day: 'verse',
        reflection_reminders: 'reflection',
      };
      for (const [key, type] of Object.entries(typeMap)) {
        if (updates[key] === true) {
          const timeKey = `${key.replace('_reminders', '').replace('_of_day', '')}_reminder_time`;
          const time = newSettings[timeKey] || '';
          toast.success(lang === 'hr' 
            ? `Podsjetnik aktiviran za ${time}` 
            : `Reminder set for ${time}`);
          // Send test notification to confirm it works
          setTimeout(() => sendScheduledNotification(type, lang), 1500);
          break;
        }
      }
      // Restart scheduler with new settings
      startNotificationScheduler(newSettings, lang);
    }
  };

  const handleTogglePushNotifications = async (val) => {
    if (val) { await subscribeToPush(); } else { await unsubscribeFromPush(); }
  };

  const handleTestPush = async () => {
    setTestPushLoading(true);
    try {
      await sendTestPush();
      toast.success(lang === 'hr' ? 'Test notifikacija poslana!' : 'Test notification sent!');
    } catch (error) {
      toast.error(lang === 'hr' ? `Greška: ${error.message}` : `Error: ${error.message}`);
    } finally {
      setTestPushLoading(false);
    }
  };

  const handleTestScheduled = async (type) => {
    try {
      await sendScheduledNotification(type, lang);
      toast.success(lang === 'hr' ? 'Notifikacija poslana!' : 'Notification sent!');
    } catch (error) {
      toast.error(lang === 'hr' ? 'Greška' : 'Error');
    }
  };

  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    try {
      await sendTestEmail();
      toast.success(lang === 'hr' ? 'Test email poslan! Provjerite inbox.' : 'Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(lang === 'hr' ? `Greška: ${error.message}` : `Error: ${error.message}`);
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleSendWeeklyReport = async () => {
    setWeeklyReportLoading(true);
    try {
      const result = await sendWeeklyReportEmail();
      if (result.sent > 0) {
        toast.success(lang === 'hr' ? 'Tjedno izvješće poslano na vaš email!' : 'Weekly report sent to your email!');
      } else {
        toast.info(lang === 'hr' ? 'Prvo omogućite tjedno izvješće' : 'Enable weekly report first');
      }
    } catch (error) {
      toast.error(lang === 'hr' ? `Greška: ${error.message}` : `Error: ${error.message}`);
    } finally {
      setWeeklyReportLoading(false);
    }
  };

  const handleToggleDarkMode = (val) => {
    setDarkMode(val);
    localStorage.setItem('darkMode', String(val));
    document.documentElement.classList.toggle('dark', val);
    toast.success(t('settings_saved'));
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    toast.success(t('settings_saved'));
  };

  const updateBarakahProfileField = (field, value) => {
    setBarakahProfile((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const toggleFocusArea = (area) => {
    setBarakahProfile((prev) => {
      const current = Array.isArray(prev?.focus_areas) ? prev.focus_areas : [];
      const updated = current.includes(area)
        ? current.filter((entry) => entry !== area)
        : [...current, area];
      return { ...(prev || {}), focus_areas: updated };
    });
  };

  const handleSaveBarakahProfile = async () => {
    if (!barakahProfile) return;
    try {
      setBarakahProfileSaving(true);
      const saved = await saveBarakahProfile(barakahProfile);
      setBarakahProfile(saved);
      toast.success(lang === 'hr' ? 'Barakah profil spremljen.' : 'Barakah profile saved.');
    } catch (error) {
      console.error('Error saving barakah profile:', error);
      toast.error(lang === 'hr' ? 'Spremanje nije uspjelo.' : 'Save failed.');
    } finally {
      setBarakahProfileSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) return;
    if (newPassword !== confirmPassword) {
      toast.error(t('passwords_no_match'));
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Minimum 6 znakova / Minimum 6 characters');
      return;
    }
    setPwLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      if (signInError) {
        toast.error(lang === 'hr' ? 'Trenutna lozinka nije ispravna' : 'Current password is incorrect');
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success(t('password_updated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(t('password_error'));
    } finally {
      setPwLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!notificationEmail || notificationEmail === user?.email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(notificationEmail)) {
      toast.error(lang === 'hr' ? 'Neispravna email adresa' : 'Invalid email address');
      return;
    }
    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: notificationEmail });
      if (error) throw error;
      toast.success(lang === 'hr' 
        ? 'Potvrda poslana na novu email adresu. Provjerite inbox.' 
        : 'Confirmation sent to new email. Check your inbox.');
    } catch (err) {
      toast.error(lang === 'hr' ? `Greška: ${err.message}` : `Error: ${err.message}`);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
    window.location.replace('/login');
  };

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto px-4 py-8 md:py-12 space-y-5">
        {/* Header */}
        <div className="mb-2">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-[#292524]">{t('profile_settings')}</h1>
              <p className="text-sm text-[#78716C]">
                {t('hi')}, {user?.name}
                {memberSince && <span className="ml-2 text-stone-400">· {t('member_since')} {memberSince}</span>}
              </p>
            </div>
          </div>
        </div>

        {isAdminEmail(user?.email) && (
          <Link
            to="/admin"
            className="flex items-center justify-between rounded-2xl border border-primary/20 bg-primary/5 px-4 py-4 hover:bg-primary/10 transition-colors"
            data-testid="profile-admin-link-card"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-primary">Admin</p>
              <p className="mt-1 text-sm text-stone-700">
                {lang === 'hr' ? 'Otvori admin pregled korisnika i aktivnosti' : 'Open admin overview for users and activity'}
              </p>
            </div>
            <Shield className="w-5 h-5 text-primary" />
          </Link>
        )}

        {/* ============ NOTIFICATIONS SECTION (FIRST) ============ */}
        <SectionCard title={lang === 'hr' ? 'Obavijesti' : 'Notifications'} icon={Bell} iconBg="bg-stone-50" defaultOpen={true}>
          {/* Push Master Toggle */}
          <NotifSettingRow
            icon={Bell}
            iconColor="bg-primary"
            title={lang === 'hr' ? 'Push Notifikacije' : 'Push Notifications'}
            description={lang === 'hr' ? 'Aktiviraj primanje obavijesti na ovom uređaju' : 'Enable receiving notifications on this device'}
          >
            <Toggle checked={pushSubscribed} onChange={handleTogglePushNotifications} testId="toggle-push" />
          </NotifSettingRow>

          {pushSubscribed && (
            <div className="py-2 border-b border-stone-100">
              <button
                onClick={handleTestPush}
                disabled={testPushLoading}
                data-testid="test-push-btn"
                className="w-full py-2 bg-primary/10 text-primary rounded-lg font-medium text-xs hover:bg-primary/20 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
              >
                {testPushLoading ? (
                  <span className="w-3.5 h-3.5 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                ) : (
                  <Bell className="w-3.5 h-3.5" />
                )}
                {lang === 'hr' ? 'Pošalji test notifikaciju' : 'Send test notification'}
              </button>
            </div>
          )}

          {!pushSubscribed && (
            <div className="mx-0 my-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs text-amber-700 text-center">
                {lang === 'hr' 
                  ? 'Aktivirajte push notifikacije iznad da biste primali podsjetnike na vrijeme' 
                  : 'Enable push notifications above to receive reminders on time'}
              </p>
            </div>
          )}

          {/* Notification Categories with Time Pickers - ALWAYS VISIBLE */}
          <div className="py-2">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2 px-1">
              {lang === 'hr' ? 'Raspored obavijesti' : 'Notification Schedule'}
            </p>
          </div>

          <NotifSettingRow
                icon={Sparkles}
                iconColor="bg-amber-500"
                title={lang === 'hr' ? 'Podsjetnik za namaz' : 'Prayer Reminder'}
                description={lang === 'hr' ? 'Dnevni podsjetnik za namaz' : 'Daily prayer reminder'}
                showTime={notifSettings.prayer_reminders}
                time={notifSettings.prayer_reminder_time}
                onTimeChange={(val) => updateNotifSetting({ prayer_reminder_time: val })}
              >
                <Toggle 
                  checked={notifSettings.prayer_reminders} 
                  onChange={(val) => updateNotifSetting({ prayer_reminders: val })}
                  testId="toggle-prayer-reminder"
                />
              </NotifSettingRow>

              <NotifSettingRow
                icon={Shield}
                iconColor="bg-emerald-500"
                title={lang === 'hr' ? 'Zikr podsjetnik' : 'Dhikr Reminder'}
                description={lang === 'hr' ? 'Jutarnji podsjetnik za zikr' : 'Morning dhikr reminder'}
                showTime={notifSettings.dhikr_reminders}
                time={notifSettings.dhikr_reminder_time}
                onTimeChange={(val) => updateNotifSetting({ dhikr_reminder_time: val })}
              >
                <Toggle 
                  checked={notifSettings.dhikr_reminders} 
                  onChange={(val) => updateNotifSetting({ dhikr_reminders: val })}
                  testId="toggle-dhikr-reminder"
                />
              </NotifSettingRow>

              <NotifSettingRow
                icon={BookOpen}
                iconColor="bg-sky-500"
                title={lang === 'hr' ? 'Ajet dana' : 'Verse of the Day'}
                description={lang === 'hr' ? 'Dnevni Kur\'anski ajet' : 'Daily Quran verse'}
                showTime={notifSettings.verse_of_day}
                time={notifSettings.verse_reminder_time}
                onTimeChange={(val) => updateNotifSetting({ verse_reminder_time: val })}
              >
                <Toggle 
                  checked={notifSettings.verse_of_day} 
                  onChange={(val) => updateNotifSetting({ verse_of_day: val })}
                  testId="toggle-verse-reminder"
                />
              </NotifSettingRow>

              <NotifSettingRow
                icon={PenLine}
                iconColor="bg-violet-500"
                title={lang === 'hr' ? 'Večernja refleksija' : 'Evening Reflection'}
                description={lang === 'hr' ? 'Podsjetnik za pisanje dnevnika' : 'Journal writing reminder'}
                showTime={notifSettings.reflection_reminders}
                time={notifSettings.reflection_reminder_time}
                onTimeChange={(val) => updateNotifSetting({ reflection_reminder_time: val })}
              >
                <Toggle 
                  checked={notifSettings.reflection_reminders} 
                  onChange={(val) => updateNotifSetting({ reflection_reminders: val })}
                  testId="toggle-reflection-reminder"
                />
              </NotifSettingRow>

          {/* Weekly Email Report */}
          <div className="pt-2">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-1 px-1">
              {lang === 'hr' ? 'Email izvješća' : 'Email Reports'}
            </p>
          </div>

          <NotifSettingRow
            icon={Calendar}
            iconColor="bg-purple-500"
            title={lang === 'hr' ? 'Tjedno Izvješće' : 'Weekly Report'}
            description={lang === 'hr' ? 'Tjedni pregled Bereket statistike na email' : 'Weekly Bereket stats via email'}
          >
            <Toggle 
              checked={notifSettings.weekly_report_enabled} 
              onChange={(val) => {
                updateNotifSetting({ weekly_report_enabled: val });
                if (val) toast.success(lang === 'hr' ? 'Tjedno izvješće aktivirano!' : 'Weekly report activated!');
              }}
              testId="toggle-weekly-report"
            />
          </NotifSettingRow>
          
          {notifSettings.weekly_report_enabled && (
            <div className="py-2 space-y-2 border-b border-stone-100">
              <button
                onClick={handleSendWeeklyReport}
                disabled={weeklyReportLoading}
                data-testid="send-weekly-report-btn"
                className="w-full py-2 bg-purple-50 text-purple-700 rounded-lg font-medium text-xs hover:bg-purple-100 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
              >
                {weeklyReportLoading ? (
                  <span className="w-3.5 h-3.5 border-2 border-purple-400 border-t-purple-700 rounded-full animate-spin" />
                ) : (
                  <Mail className="w-3.5 h-3.5" />
                )}
                {lang === 'hr' ? 'Pošalji izvješće sada' : 'Send report now'}
              </button>
              <button
                onClick={handleTestEmail}
                disabled={testEmailLoading}
                data-testid="test-email-btn"
                className="w-full py-2 bg-stone-50 text-stone-600 rounded-lg font-medium text-xs hover:bg-stone-100 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
              >
                {testEmailLoading ? (
                  <span className="w-3.5 h-3.5 border-2 border-stone-400 border-t-stone-700 rounded-full animate-spin" />
                ) : (
                  <Mail className="w-3.5 h-3.5" />
                )}
                {lang === 'hr' ? 'Pošalji test email' : 'Send test email'}
              </button>
            </div>
          )}
        </SectionCard>

        {/* ============ APPEARANCE ============ */}
        <SectionCard title={t('appearance')} icon={Moon} iconBg="bg-stone-50" defaultOpen={true}>
          <div className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Moon className="w-3.5 h-3.5 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium text-[#292524] text-sm">{t('dark_mode')}</p>
                  <p className="text-xs text-[#78716C]">{t('dark_mode_desc')}</p>
                </div>
              </div>
              <Toggle checked={darkMode} onChange={handleToggleDarkMode} testId="toggle-dark-mode" />
            </div>
          </div>
        </SectionCard>

        {/* ============ LANGUAGE ============ */}
        <SectionCard title={t('language_settings')} icon={Globe} iconBg="bg-stone-50" defaultOpen={true}>
          <div className="py-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { code: 'en', label: t('english'), flag: '🇬🇧' },
                { code: 'hr', label: t('croatian'), flag: '🇧🇦' },
              ].map(({ code, label, flag }) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  data-testid={`lang-${code}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                    i18n.language === code
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-stone-200 bg-stone-50 text-[#57534E] hover:border-stone-300'
                  }`}
                >
                  <span className="text-xl">{flag}</span>
                  <span className="font-medium text-sm">{label}</span>
                  {i18n.language === code && <Check className="w-4 h-4 ml-auto" />}
                </button>
              ))}
            </div>
          </div>
        </SectionCard>

        <SectionCard title={lang === 'hr' ? 'Barakah profil' : 'Barakah profile'} icon={Sparkles} iconBg="bg-stone-50" defaultOpen={true}>
          <div className="py-4 space-y-4" data-testid="profile-barakah-profile-card">
            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">
                {lang === 'hr' ? 'Prozor budnosti i sna' : 'Wake & sleep window'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="time"
                  value={barakahProfile?.wake_window_start || '05:30'}
                  onChange={(e) => updateBarakahProfileField('wake_window_start', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm"
                  data-testid="profile-barakah-wake-start-input"
                />
                <input
                  type="time"
                  value={barakahProfile?.sleep_window_end || '23:30'}
                  onChange={(e) => updateBarakahProfileField('sleep_window_end', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm"
                  data-testid="profile-barakah-sleep-end-input"
                />
              </div>
            </div>

            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">
                {lang === 'hr' ? 'Radno vrijeme' : 'Work hours'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="time"
                  value={barakahProfile?.work_window_start || '09:00'}
                  onChange={(e) => updateBarakahProfileField('work_window_start', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm"
                  data-testid="profile-barakah-work-start-input"
                />
                <input
                  type="time"
                  value={barakahProfile?.work_window_end || '17:00'}
                  onChange={(e) => updateBarakahProfileField('work_window_end', e.target.value)}
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm"
                  data-testid="profile-barakah-work-end-input"
                />
              </div>
            </div>

            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">
                {lang === 'hr' ? 'Područja fokusa' : 'Focus areas'}
              </p>
              <div className="flex flex-wrap gap-2">
                {FOCUS_AREAS.map((area) => {
                  const active = (barakahProfile?.focus_areas || []).includes(area);
                  return (
                    <button
                      key={area}
                      type="button"
                      onClick={() => toggleFocusArea(area)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-primary text-white border-primary'
                          : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-stone-300'
                      }`}
                      data-testid={`profile-barakah-focus-${area.toLowerCase()}`}
                    >
                      {area}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={barakahProfile?.city || ''}
                onChange={(e) => updateBarakahProfileField('city', e.target.value)}
                placeholder={lang === 'hr' ? 'Grad' : 'City'}
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm"
                data-testid="profile-barakah-city-input"
              />
              <input
                type="text"
                value={barakahProfile?.timezone || ''}
                onChange={(e) => updateBarakahProfileField('timezone', e.target.value)}
                placeholder={lang === 'hr' ? 'Vremenska zona' : 'Timezone'}
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl text-sm"
                data-testid="profile-barakah-timezone-input"
              />
            </div>

            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">
                {lang === 'hr' ? 'Preferencija navika' : 'Habit preference'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => updateBarakahProfileField('habit_preference', 'small')}
                  className={`px-3 py-2 rounded-xl border text-sm ${barakahProfile?.habit_preference === 'small' ? 'bg-primary text-white border-primary' : 'bg-stone-50 border-stone-200 text-stone-600'}`}
                  data-testid="profile-barakah-habit-small-button"
                >
                  {lang === 'hr' ? 'Manje navike' : 'Small habits'}
                </button>
                <button
                  type="button"
                  onClick={() => updateBarakahProfileField('habit_preference', 'challenging')}
                  className={`px-3 py-2 rounded-xl border text-sm ${barakahProfile?.habit_preference === 'challenging' ? 'bg-primary text-white border-primary' : 'bg-stone-50 border-stone-200 text-stone-600'}`}
                  data-testid="profile-barakah-habit-challenging-button"
                >
                  {lang === 'hr' ? 'Izazovne navike' : 'Challenging habits'}
                </button>
              </div>
            </div>

            <button
              onClick={handleSaveBarakahProfile}
              disabled={barakahProfileSaving}
              className="w-full py-2.5 rounded-xl bg-primary text-white font-medium text-sm hover:bg-primary/90 disabled:opacity-50"
              data-testid="profile-barakah-save-button"
            >
              {barakahProfileSaving
                ? (lang === 'hr' ? 'Spremanje...' : 'Saving...')
                : (lang === 'hr' ? 'Spremi barakah profil' : 'Save barakah profile')}
            </button>
          </div>
        </SectionCard>

        {/* ============ ACCOUNT (Email + Password - collapsed by default) ============ */}
        <SectionCard title={t('account')} icon={Lock} iconBg="bg-stone-50" defaultOpen={false}>
          <div className="py-4 space-y-4">
            {/* Email Change */}
            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">
                {lang === 'hr' ? 'Email adresa' : 'Email Address'}
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="email"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    placeholder={lang === 'hr' ? 'Email adresa' : 'Email address'}
                    data-testid="email-input"
                    className="w-full px-4 py-2.5 pl-10 border border-stone-200 rounded-xl text-sm text-[#292524] bg-stone-50 focus:outline-none focus:border-primary focus:bg-white transition-colors"
                  />
                </div>
                <button
                  onClick={handleChangeEmail}
                  disabled={emailLoading || notificationEmail === user?.email || !notificationEmail}
                  data-testid="change-email-btn"
                  className="px-4 py-2.5 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  {emailLoading ? (
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Password Change */}
            <div>
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-2">
                {t('change_password')}
              </p>
              <div className="space-y-2">
                <div className="relative">
                  <input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('current_password')}
                    className="w-full px-4 py-2.5 pr-11 border border-stone-200 rounded-xl text-sm text-[#292524] bg-stone-50 focus:outline-none focus:border-primary focus:bg-white transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('new_password')}
                      className="w-full px-4 py-2.5 pr-11 border border-stone-200 rounded-xl text-sm text-[#292524] bg-stone-50 focus:outline-none focus:border-primary focus:bg-white transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showConfirmPw ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('confirm_new_password')}
                      className="w-full px-4 py-2.5 pr-11 border border-stone-200 rounded-xl text-sm text-[#292524] bg-stone-50 focus:outline-none focus:border-primary focus:bg-white transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPw(!showConfirmPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={pwLoading || !currentPassword || !newPassword || !confirmPassword}
                  data-testid="change-password-btn"
                  className="w-full py-2.5 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {pwLoading ? (
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4" />
                  )}
                  {t('update_password')}
                </button>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ============ SIGN OUT ============ */}
        <div className="bg-white/80 backdrop-blur-sm border border-red-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-6 py-4">
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center justify-center gap-3 py-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl font-medium text-sm transition-all border border-red-100"
            >
              <LogOut className="w-4 h-4" />
              {t('sign_out')}
            </button>
          </div>
        </div>

        {/* Support Footer */}
        <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/10 rounded-2xl p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <MessageCircle className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-[#292524] text-sm">
              {lang === 'hr' ? 'Podrška i kontakt' : 'Support & Contact'}
            </h3>
          </div>
          <p className="text-xs text-[#57534E] mb-3">
            {lang === 'hr' ? 'Za pitanja, prijedloge ili pomoć:' : 'For questions, suggestions, or help:'}
          </p>
          <a 
            href="mailto:app@islam-iznutra.com"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 transition-all shadow-sm"
          >
            <Mail className="w-4 h-4" />
            app@islam-iznutra.com
          </a>
        </div>
      </div>
    </Layout>
  );
}
